import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { useHistory, useLocation } from "react-router";
import { OrgContext } from "contexts/OrgContext";
import { CloudCredContext } from "contexts/CloudCredContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import { GPUInstanceType } from "types/InstanceTypes";
import { isCheckHealthSame, parseStatusMessage } from "components/utils";
import {
  IApplication,
  VerbBuildStatus,
  WorkspaceStatus,
} from "models/Workspace.model";
import agent, {
  type LaunchableParameterBinding,
  type NGCAPIKeySpec,
} from "server";
import { VERB_FAILED_PHRASE, VERB_FINISHED_PHRASE } from "utils/verb";
import { toast } from "utils/toast";
import { UserContext } from "contexts/UserContext";
import { BillingContext } from "./BillingContext";
import {
  allPublicCloudsName,
  handleIsPaymentSetup,
  hasVerbBaseImageContainer,
} from "components/Environment/Create/utils";
import {
  isFreeTierInstance,
  getStoragePricing,
  useFreeTier,
} from "utils/freeTierUtils";
import { buildDefaultVerbYamlConfig } from "components/Verb/utils";
import yaml from "js-yaml";
import {
  findNotebookApp,
  hasCTAIngress,
  isCtaServiceReady,
  isJupyterDeployment,
  isLifecycleScriptTerminal,
} from "components/Environment/utils";
import { useHealthCheck } from "./useHealthCheck";
import {
  ProcessedLaunchableData,
  createFileRequest,
} from "components/Launchables/utils";
import {
  ContainerSelected,
  DockerCompose,
  LifecycleScriptStatus,
} from "components/Environment/shared/BuildTypes";
import { Environment } from "components/Environment/utils/types";
import { EnvironmentStatus } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import { useQuery } from "@tanstack/react-query";
import {
  getEnvironmentStatus,
  getHealthChecksFromEnvironment,
  getIngressApplicationsFromEnvironment,
  getVerbBuildStatusFromTasks,
  isEnvironmentBuildSame,
  isLifeCycleScriptsSame,
  normalizeEnvironmentStatus,
  transformWorkspaceToEnvironment,
} from "components/Environment/utils/environtmentUtils";
import { CreateFirewallRule } from "components/Environment/Create/ExposePorts";
import posthog from "posthog-js";
import {
  getPrimaryInstanceCloudCred,
  getPrimaryInstanceCloudCredId,
} from "utils/cloudCreds";
import { ensureNGCEnablementForAPIKey } from "utils/ngcEnablement";
import {
  getEnvironmentLogTarget,
  useEnvironmentLogs,
} from "server/devplane/useEnvironmentLogs";
import {
  fetchLatestPersistedEnvironmentLogsByType,
  ENVIRONMENT_LOG_TYPES,
} from "server/devplane/environmentLogs";
import {
  POSTHOG_IDENTITY_FLAGS,
  useIdentityFeatureFlagEnabled,
} from "utils/posthogFeatureFlags";

export enum DeployState {
  UNSET = "",
  Deploying = "deploying",
  Dependencies = "building",
  StartupScript = "startup",
  HealthCheck = "health_check",
  Ready = "ready",
}

export type DeployStep = {
  id: number;
  name: string;
  /** @deprecated Used only by the legacy nav-bar progress component (DeployProgress). */
  legacyName?: string;
  completedName: string;
  abbreviatedName: string;
  /** @deprecated Used only by the legacy nav-bar progress component (DeployProgress). */
  legacyAbbreviatedName?: string;
  abbreviatedName_future: string;
  /** @deprecated Used only by the legacy nav-bar progress component (DeployProgress). */
  legacyAbbreviatedName_future?: string;
  detail?: string;
  state: DeployState;
};

const VERB_CONJUGATIONS: [present: string, past: string, future: string][] = [
  ["Deploying", "Deployed", "Deploy"],
  ["Installing", "Installed", "Install"],
  ["Starting", "Started", "Start"],
  ["Building", "Built", "Build"],
  ["Preparing", "Prepared", "Prepare"],
  ["Configuring", "Configured", "Configure"],
  ["Running", "Ran", "Run"],
];

export type StartDeploymentResult = {
  deploymentStarted: boolean;
  ngcApiKeySpecFailed?: boolean;
};

const deriveCompletedName = (name: string): string =>
  VERB_CONJUGATIONS.reduce(
    (n, [present, past]) => n.replace(new RegExp(`^${present}\\b`), past),
    name,
  );

const deriveFutureName = (name: string): string =>
  VERB_CONJUGATIONS.reduce(
    (n, [present, , future]) => n.replace(new RegExp(`^${present}\\b`), future),
    name,
  );

const getDeploySteps = (
  isCPU: boolean,
  hasJupyter: boolean,
  hasStartupScript = false,
  hasHealthCheck = false,
  dependencyName?: string,
  dependencyDetail?: string,
  legacyDependencyName?: string,
) => {
  const hwLabel = isCPU ? "CPU" : "GPU";
  const installName =
    dependencyName ??
    (hasJupyter
      ? isCPU
        ? "Installing Python 3.10"
        : "Installing Python 3.10 & CUDA 12.2"
      : "Installing Software");
  const installCompletedName = dependencyName
    ? deriveCompletedName(dependencyName)
    : hasJupyter
      ? isCPU
        ? "Installed Python 3.10"
        : "Installed Python 3.10 & CUDA 12.2"
      : "Installed Software";

  const steps: DeployStep[] = [
    {
      id: 0,
      name: `Provisioning ${hwLabel} instance`,
      legacyName: `Deploying ${hwLabel}`,
      completedName: `Provisioned ${hwLabel} instance`,
      abbreviatedName: `Provisioned ${hwLabel}`,
      legacyAbbreviatedName: `Deployed ${hwLabel}`,
      abbreviatedName_future: `Provision ${hwLabel}`,
      legacyAbbreviatedName_future: `Deploy ${hwLabel}`,
      state: DeployState.Deploying,
    },
    {
      id: 1,
      name: installName,
      legacyName: legacyDependencyName,
      completedName: installCompletedName,
      abbreviatedName: "Installed",
      legacyAbbreviatedName_future:
        legacyDependencyName !== undefined ? "Install Software" : undefined,
      abbreviatedName_future: dependencyName
        ? deriveFutureName(dependencyName)
        : "Install Software",
      detail: dependencyDetail,
      state: DeployState.Dependencies,
    },
  ];
  if (hasStartupScript) {
    steps.push({
      id: 2,
      name: "Running startup script",
      completedName: "Ran startup script",
      abbreviatedName: "Startup script complete",
      abbreviatedName_future: "Run startup script",
      state: DeployState.StartupScript,
    });
  }
  if (hasHealthCheck) {
    steps.push({
      id: steps.length,
      name: "Checking service status",
      completedName: "Services are healthy",
      abbreviatedName: "Services are healthy",
      abbreviatedName_future: "Check service status",
      state: DeployState.HealthCheck,
    });
  }
  const readyId = steps.length;
  const readyLabel = hasJupyter
    ? "Access Notebook"
    : isCPU
      ? "Access CPU"
      : "Access GPU";
  steps.push({
    id: readyId,
    name: "Ready",
    completedName: "Ready",
    abbreviatedName: readyLabel,
    abbreviatedName_future: readyLabel,
    state: DeployState.Ready,
  });
  return steps;
};

const DEPLOY_STATE_ORDER = [
  DeployState.UNSET,
  DeployState.Deploying,
  DeployState.Dependencies,
  DeployState.StartupScript,
  DeployState.HealthCheck,
  DeployState.Ready,
];

const getDeployStateIndex = (step: DeployState): number =>
  DEPLOY_STATE_ORDER.indexOf(step);

const getFurthestDeployState = (
  currentStep: DeployState,
  nextStep: DeployState,
): DeployState =>
  getDeployStateIndex(nextStep) > getDeployStateIndex(currentStep)
    ? nextStep
    : currentStep;

export const getDeployStateFromEnvironment = ({
  environment,
  hasStartupScript,
  hasHealthCheck,
  buildFinishedFromLogs = false,
}: {
  environment: Environment | undefined;
  hasStartupScript: boolean;
  hasHealthCheck: boolean;
  buildFinishedFromLogs?: boolean;
}): DeployState => {
  if (!environment) return DeployState.UNSET;

  if (
    normalizeEnvironmentStatus(environment.status) ===
    EnvironmentStatus.BUILDING
  ) {
    return DeployState.Dependencies;
  }

  if (getEnvironmentStatus(environment) !== WorkspaceStatus.Running) {
    return DeployState.Deploying;
  }

  const buildCompleted =
    buildFinishedFromLogs ||
    getVerbBuildStatusFromTasks(environment.tasks || []) ===
      VerbBuildStatus.Completed;
  if (!buildCompleted) return DeployState.Dependencies;

  const scriptStatus = environment.lifecycle_scripts?.[0]?.last_run?.status;
  if (hasStartupScript && !isLifecycleScriptTerminal(scriptStatus)) {
    return DeployState.StartupScript;
  }

  if (
    hasHealthCheck &&
    environment.labels?.["tunnel-provider"] !== "skybridge" &&
    !isCtaServiceReady(environment)
  ) {
    return DeployState.HealthCheck;
  }

  return DeployState.Ready;
};

const hasIngressGatewayStatusChanged = (
  updatedEnvironment: Environment,
  currentEnvironment: Environment,
): boolean =>
  (updatedEnvironment.ingress_gateways ?? []).some(
    (gateway, index) =>
      gateway.status !== currentEnvironment.ingress_gateways?.[index]?.status,
  );

const hasPolledEnvironmentChanged = (
  updatedEnvironment: Environment,
  currentEnvironment: Environment,
): boolean =>
  updatedEnvironment.tasks?.length !== currentEnvironment.tasks?.length ||
  updatedEnvironment.instance?.status?.object_status !==
    currentEnvironment.instance?.status?.object_status ||
  updatedEnvironment.instance?.status?.lifecycle_status !==
    currentEnvironment.instance?.status?.lifecycle_status ||
  updatedEnvironment.status !== currentEnvironment.status ||
  !isEnvironmentBuildSame(updatedEnvironment.build, currentEnvironment.build) ||
  !isCheckHealthSame(
    getHealthChecksFromEnvironment(updatedEnvironment),
    getHealthChecksFromEnvironment(currentEnvironment),
  ) ||
  getVerbBuildStatusFromTasks(updatedEnvironment.tasks || []) !==
    getVerbBuildStatusFromTasks(currentEnvironment.tasks || []) ||
  !isLifeCycleScriptsSame(
    updatedEnvironment.lifecycle_scripts,
    currentEnvironment.lifecycle_scripts,
  ) ||
  hasIngressGatewayStatusChanged(updatedEnvironment, currentEnvironment);

interface OnClickVerbConfiguration {
  baseImage: string;
  cuda: string;
}

const oneClickDeployDefaultValues = {
  activeStep: DeployState.Deploying,
  setActiveStep: (step: DeployState) => null,
  steps: [],
  readyAction: () => null,
  hasNotebook: true,
  notebookApp: undefined as IApplication | undefined,
  startDeployment: (
    launchableId: string,
    instance,
    storage,
    instanceName,
    file,
    baseImage,
    cuda,
    python,
    ports: Record<string, string>,
    firewallRules: CreateFirewallRule[] | null,
    extraEventProperties: Record<string, any>,
    location?: string | null,
    subLocation?: string | null,
    hasCTAApp?: boolean,
    ngcApiKeySpec?: NGCAPIKeySpec | null,
    parameterBindings?: LaunchableParameterBinding[],
    cloudCredentialId?: string | null,
  ) => Promise.resolve({ deploymentStarted: false }),
  startDeploymentFromLaunchable: (queryParams) =>
    Promise.resolve({ deploymentStarted: false }),
  setIsFinished: (isFinished: boolean) => null,
  isFinished: false,
  reset: () => null,
  setShowDeploying: (isShowing: boolean) => null,
  showDeploying: false,
  showConnectCloudsOrCardModal: false,
  setShowConnectCloudsOrCardModal: (show: boolean) => null,
  noCardOnFile: true,
  setNoCardOnFile: (noCardOnFile: boolean) => null,
  selectedCloudName: allPublicCloudsName,
  logFileContent: "",
  setSelectedCloudName: (selectedCloudName: string) => null,
  environment: undefined,
  instanceTypeObject: undefined,
  requestedDiskStorage: 128,
  initLoading: false,
  healthCheckFailed: false,
};

interface OneClickContext {
  activeStep: DeployState;
  setActiveStep: (step: DeployState) => void;
  steps: DeployStep[];
  readyAction: (notebookHostname?: string) => void;
  hasNotebook: boolean;
  notebookApp: IApplication | undefined;
  startDeployment: (
    launchableId: string,
    instance,
    storage,
    instanceName,
    file,
    baseImage,
    cuda,
    python,
    ports: Record<string, string>,
    firewallRules: CreateFirewallRule[] | null,
    extraEventProperties: Record<string, any>,
    location?: string | null,
    subLocation?: string | null,
    hasCTAApp?: boolean,
    ngcApiKeySpec?: NGCAPIKeySpec | null,
    parameterBindings?: LaunchableParameterBinding[],
    cloudCredentialId?: string | null,
  ) => Promise<StartDeploymentResult | void>;
  startDeploymentFromLaunchable: (
    launchableData: ProcessedLaunchableData | null,
    extraEventProperties,
    workspaceName: string,
    ngcApiKeySpec?: NGCAPIKeySpec | null,
    parameterBindings?: LaunchableParameterBinding[],
  ) => Promise<StartDeploymentResult | void>;
  setIsFinished: (isFinished: boolean) => void;
  isFinished: boolean;
  reset: () => void;
  setShowDeploying: (isShowing: boolean) => void;
  showDeploying: boolean;
  showConnectCloudsOrCardModal: boolean;
  setShowConnectCloudsOrCardModal: (show: boolean) => void;
  noCardOnFile: boolean;
  setNoCardOnFile: (noCardOnFile: boolean) => void;
  selectedCloudName: string;
  setSelectedCloudName: (selectedCloudName: string) => void;
  environment: Environment | undefined;
  instanceTypeObject: GPUInstanceType | undefined;
  requestedDiskStorage: number;
  initLoading: boolean;
  logFileContent: string;
  healthCheckFailed: boolean;
}

const pollInstanceUntilSuccess = async (
  workspaceID: string,
  interval = 5000,
  timeout = 60000 * 10,
) => {
  let elapsedTime = 0;

  while (elapsedTime < timeout) {
    try {
      const res = await agent.Workspaces.get(workspaceID);
      if (
        res.success &&
        res.data &&
        res.data.status === WorkspaceStatus.Running
      ) {
        return res.data;
      } else {
        throw new Error("Workspace not ready yet");
      }
    } catch (error) {
      console.error("Error polling API:", error);

      // Wait for the specified interval before retrying
      await new Promise((resolve) => setTimeout(resolve, interval));
      elapsedTime += interval;
    }
  }

  // If the loop exits due to timeout, throw an error
  throw new Error("Polling timed out");
};

export const OneClickDeployContext = React.createContext<OneClickContext>(
  oneClickDeployDefaultValues,
);

const pruneDockerCompose = (dockerCompose: DockerCompose) => {
  return {
    ...dockerCompose,
    yamlString: dockerCompose.fileUrl ? undefined : dockerCompose.yamlString,
    parsedYaml: undefined,
    registries: [],
  };
};

interface Props {
  children: React.ReactNode;
}
const OneClickDeployContextProvider: React.FC<Props> = ({ children }) => {
  const orgContext = useContext(OrgContext);
  const { cloudCreds } = useContext(CloudCredContext);
  const instanceTypesContext = useContext(InstanceTypesContext);
  const workspaceContext = useContext(WorkspaceContext);
  const userContext = useContext(UserContext);
  const { isEntitled, isLoading: isFreeTierLoading } = useFreeTier();
  const query = new URLSearchParams(useLocation().search);
  const environmentLogHistoryEnabled =
    useIdentityFeatureFlagEnabled(
      POSTHOG_IDENTITY_FLAGS.INSTANCE_EXEC_LOG_HISTORY,
    ) === true;

  const [showDeploying, setShowDeploying] = useState(false);
  const [isFinished, setIsFinished] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [logFileContent, setLogFileContent] = useState("");
  const buildFinishedFromLogsRef = useRef(false);
  const billingContext = useContext(BillingContext);
  const [showConnectCloudsOrCardModal, setShowConnectCloudsOrCardModal] =
    useState(false);
  const [noCardOnFile, setNoCardOnFile] = useState(false);
  const [selectedCloudName, setSelectedCloudName] =
    useState(allPublicCloudsName);

  const [name, setName] = useState("");
  const [initLoading, setInitLoading] = useState(false);
  const [steps, setSteps] = useState(getDeploySteps(false, true));
  const [hasNotebook, setHasNotebook] = useState(true);
  const hasStartupScriptRef = useRef(false);
  const hasHealthCheckRef = useRef(false);
  const history = useHistory();

  const [environment, setEnvironment] = useState<Environment | undefined>();
  const [activeStep, setActiveStep] = useState(DeployState.UNSET);
  const [notebookApp, setNotebookApp] = useState<IApplication>();
  const [requestedDiskStorage, setRequestedDiskStorage] = useState(128);
  const [instanceTypeObject, setInstanceTypeObject] =
    useState<GPUInstanceType>();
  const { healthCheckFailed, reset: resetHealthCheck } = useHealthCheck(
    activeStep === DeployState.HealthCheck,
    environment,
  );

  const reset = useCallback(() => {
    setName("");
    setInitLoading(false);
    setActiveStep(DeployState.UNSET);
    setIsFinished(true);
    setNotebookApp(undefined);
    setEnvironment(undefined);
    setLogFileContent("");
    buildFinishedFromLogsRef.current = false;
    setShowDeploying(false);
    setErrorMessage("");
    setHasNotebook(true);
    hasStartupScriptRef.current = false;
    hasHealthCheckRef.current = false;
    resetHealthCheck();
  }, [resetHealthCheck]);

  const onFail = useCallback(
    (message: string) => {
      reset();
      toast.error("Instance Failure:", { description: message });
    },
    [reset],
  );

  const updateDependencyList = (
    newName: string,
    steps: DeployStep[],
    detail?: string,
  ) => {
    setSteps(
      steps.map((item) =>
        item.id === 1
          ? {
              ...item,
              name: newName,
              completedName: deriveCompletedName(newName),
              detail,
            }
          : item,
      ),
    );
  };

  const getCurrentDeployStateFromEnvironment = useCallback(
    (nextEnvironment: Environment | undefined, buildFinishedFromLogs = false) =>
      getDeployStateFromEnvironment({
        environment: nextEnvironment,
        hasStartupScript: hasStartupScriptRef.current,
        hasHealthCheck: hasHealthCheckRef.current,
        buildFinishedFromLogs,
      }),
    [],
  );

  const advanceActiveStepTo = useCallback((nextStep: DeployState) => {
    setActiveStep((current) => getFurthestDeployState(current, nextStep));
  }, []);

  useEffect(() => {
    if (isFreeTierLoading || !orgContext.activeOrgId) return;

    const { withinFreeTier: isStorageWithinFreeTier } = getStoragePricing(
      instanceTypeObject,
      requestedDiskStorage,
    );
    const isAllFreeTier =
      isEntitled &&
      isFreeTierInstance(instanceTypeObject?.base_price?.amount) &&
      isStorageWithinFreeTier;
    if (isAllFreeTier) {
      setNoCardOnFile(false);
      return;
    }
    handleIsPaymentSetup(selectedCloudName, orgContext.activeOrgId).then(
      (isPaymentSetUp) => setNoCardOnFile(!isPaymentSetUp),
    );
  }, [
    orgContext.activeOrgId,
    cloudCreds,
    selectedCloudName,
    instanceTypeObject,
    isEntitled,
    isFreeTierLoading,
    requestedDiskStorage,
  ]);

  const parseInstance = (providedInstance: string | null) => {
    if (!providedInstance) {
      return null;
    }
    const [chipTypeOrInstanceType, instanceType] = providedInstance.split("@");
    if (instanceType) {
      return {
        chipType: chipTypeOrInstanceType,
        instanceType: instanceType,
      };
    } else {
      return {
        instanceType: chipTypeOrInstanceType,
      };
    }
  };

  const transformLaunchableLabels = (extraEventProperties: any) => {
    return {
      launchableId: extraEventProperties.launchableID,
      launchableInstanceType: extraEventProperties.launchableInstanceType,
      cloudCredId: extraEventProperties.cloudCredID,
      launchableCreatedByUserId: extraEventProperties.launchableCreatedByUserID,
      launchableCreatedByOrgId: extraEventProperties.launchableCreatedByOrgID,
      launchableRawURL: extraEventProperties.launchableRawURL,
    };
  };

  const startDeploymentFromLaunchable = async (
    launchableData: ProcessedLaunchableData | null,
    extraEventProperties,
    workspaceName: string,
    ngcApiKeySpec?: NGCAPIKeySpec | null,
    parameterBindings?: LaunchableParameterBinding[],
  ) => {
    if (!billingContext.billingProfile) {
      onFail(
        "Billing Profile hasn't loaded yet, Please wait a moment and try again",
      );
      return { deploymentStarted: false };
    }

    setInitLoading(true);
    const billingProfile = billingContext.billingProfile;
    const redeemCouponInDevplane = !!launchableData?.couponCode;

    const { withinFreeTier: isDeployStorageWithinFreeTier } = getStoragePricing(
      instanceTypeObject,
      requestedDiskStorage,
    );
    const deployIsFreeTier =
      isEntitled &&
      isFreeTierInstance(instanceTypeObject?.base_price?.amount) &&
      isDeployStorageWithinFreeTier;
    if (
      !deployIsFreeTier &&
      ((billingProfile?.billing_type === "usage-arrears" && noCardOnFile) ||
        (billingProfile?.billing_type === "credit" &&
          !redeemCouponInDevplane &&
          (billingProfile?.credit_details?.remaining_credits || 0) <= 0))
    ) {
      setShowConnectCloudsOrCardModal(true);
      reset();
      return { deploymentStarted: false };
    }

    const ngcEnablementReady = await ensureNGCEnablementForAPIKey({
      ngcApiKeySpec,
      activeOrg: orgContext.activeOrg,
      activeOrgId: orgContext.activeOrgId,
      refreshOrgs: orgContext.refreshOrgs,
    });
    if (!ngcEnablementReady) {
      reset();
      return { deploymentStarted: false, ngcApiKeySpecFailed: true };
    }

    if (launchableData?.diskStorage) {
      setRequestedDiskStorage(parseInt(launchableData?.diskStorage));
    }
    setName(workspaceName);
    setErrorMessage("");
    buildFinishedFromLogsRef.current = false;
    setIsFinished(false);

    if (launchableData?.firewallRules) {
      //Get Client IP
      try {
        // Fetch public IP from ipify
        const response = await fetch("https://api.ipify.org?format=json");
        if (!response.ok) throw new Error("Failed to fetch IP from api.ipify");
        const data: { ip: string } = await response.json();
        const clientIP = data.ip;
        launchableData?.firewallRules?.forEach((firewallRule) => {
          if (firewallRule.allowedIPs === "user-ip") {
            firewallRule.clientIPs = [clientIP + "/32"];
          }
        });
      } catch (err) {
        console.error("Failed to fetch or send IP", err);
      }
    }

    //Could be multiple so revisit later to still show nav bar if multiple one clicks active
    // eslint-disable-next-line no-use-before-define -- defined below to keep the launchable-specific flow first.
    return await startDeployment(
      launchableData?.launchableId || "",
      launchableData?.instanceType,
      launchableData?.diskStorage,
      workspaceName,
      launchableData?.file || "",
      launchableData?.containerSelected,
      launchableData?.cuda,
      launchableData?.python,
      launchableData?.ports || null,
      launchableData?.firewallRules || null,
      extraEventProperties,
      launchableData?.location || null,
      launchableData?.subLocation || null,
      launchableData?.hasCTAApp ?? false,
      ngcApiKeySpec,
      parameterBindings,
      launchableData?.cloudCredId,
    );
  };

  async function startDeployment(
    launchableId: string,
    instanceType,
    storage,
    instanceName,
    file = "",
    containerSelected: ContainerSelected = {
      verbBuild: {
        containerUrl: "Default",
      },
    },
    cuda,
    python,
    portMappings: Record<string, string> | null,
    firewallRules: CreateFirewallRule[] | null,
    extraEventProperties: Record<string, any> | null,
    location: string | null = null,
    subLocation: string | null = null,
    hasCTAApp: boolean = false,
    ngcApiKeySpec?: NGCAPIKeySpec | null,
    parameterBindings?: LaunchableParameterBinding[],
    cloudCredentialId?: string | null,
  ) {
    //This to help backwards compatible with oldInstanceType that uses the format chipType@instanceType
    const parsedInstance = parseInstance(instanceType);

    const selectedInstanceTypeObject =
      instanceTypesContext.allInstancesAvailable.find(
        (instance) =>
          instance.type === parsedInstance?.instanceType &&
          (!cloudCredentialId ||
            getPrimaryInstanceCloudCredId(instance) === cloudCredentialId),
      );
    setInstanceTypeObject(selectedInstanceTypeObject);
    if (!selectedInstanceTypeObject) {
      onFail("Instance is out of capacity");
      console.error("No instance type found");
      return { deploymentStarted: false };
    }

    const cloudCred = getPrimaryInstanceCloudCred(selectedInstanceTypeObject);

    if (
      !cloudCred ||
      !cloudCred.locations ||
      cloudCred.locations.length === 0
    ) {
      onFail("No locations available for this cloud credential");
      console.error("No locations available for this cloud credential");
      return { deploymentStarted: false };
    }

    const locationValue = location || "";

    let jupyterPort = 8888;

    if (instanceName.toLowerCase().includes("blueprint")) {
      jupyterPort = 8887;
    }

    const customYaml = buildDefaultVerbYamlConfig(
      hasVerbBaseImageContainer(containerSelected)
        ? containerSelected?.verbBuild?.containerUrl
        : "",
      portMappings,
      cuda && cuda !== "undefined" ? cuda : "12.0.1",
      python && python !== "undefined" ? python : "3.10",
      jupyterPort,
    );

    const isCPUInstance =
      !selectedInstanceTypeObject.supported_gpus ||
      selectedInstanceTypeObject.supported_gpus.length === 0;

    const isNoJupyter = !isJupyterDeployment(containerSelected, portMappings);

    // Compute the dependency step label from the container type. All types except
    // the default verbBuild are known before the API call, so we can show the
    // correct text from the very first render of the panel.
    let dependencyName: string | undefined;
    let dependencyDetail: string | undefined;
    let legacyDependencyName: string | undefined;
    if (containerSelected?.dockerCompose) {
      dependencyName = "Starting Docker Compose";
    } else if (containerSelected?.vmBuild) {
      dependencyName = "Configuring the instance";
      legacyDependencyName = "Preparing VM Mode";
    } else if (containerSelected?.customContainer) {
      dependencyName = "Starting docker container";
      dependencyDetail = containerSelected.customContainer.containerUrl;
    } else if (containerSelected?.verbBuild?.containerUrl !== "Default") {
      dependencyName = "Building container";
      dependencyDetail = containerSelected?.verbBuild?.containerUrl;
    }

    const hasJupyterHealthCheck = !isNoJupyter;
    const hasInitialHealthCheck = hasJupyterHealthCheck || hasCTAApp;
    const buildSteps = (hasStartupScript: boolean, hasHealthCheck: boolean) =>
      getDeploySteps(
        isCPUInstance,
        !isNoJupyter,
        hasStartupScript,
        hasHealthCheck,
        dependencyName,
        dependencyDetail,
        legacyDependencyName,
      );
    let stepsToUse = buildSteps(false, hasInitialHealthCheck);
    setSteps(stepsToUse);
    if (isNoJupyter) {
      setHasNotebook(false);
    }

    const fileRequest = createFileRequest(file, containerSelected);

    const res = await workspaceContext.createWorkspace(
      instanceName,
      orgContext.activeOrg?.id || "",
      cloudCred.id,
      "description",
      {}, // repo
      {}, // execs
      undefined, // ide config
      selectedInstanceTypeObject.type,
      "", // image
      locationValue, // location
      "",
      storage || "120",
      false, // Spot
      false, // isStoppable
      false, // on container false
      containerSelected?.verbBuild?.containerUrl ? customYaml : "", // verb yaml
      hasVerbBaseImageContainer(containerSelected)
        ? containerSelected?.verbBuild?.containerUrl
        : "", // verb container url if it's not default
      containerSelected?.customContainer
        ? {
            containerUrl: containerSelected?.customContainer?.containerUrl,
            entryPoint: containerSelected?.customContainer?.entryPoint,
            registry: containerSelected?.customContainer?.registry,
          }
        : undefined, // custom container
      containerSelected?.vmBuild, // vm build
      portMappings,
      fileRequest,
      transformLaunchableLabels(extraEventProperties),
      {
        isPublic: false,
      },
      "v1",
      containerSelected?.dockerCompose
        ? pruneDockerCompose(containerSelected.dockerCompose)
        : undefined,
      {
        id: launchableId,
        ...(parameterBindings?.length ? { parameterBindings } : {}),
      },
      firewallRules || undefined,
      subLocation || undefined,
      ngcApiKeySpec,
    );
    if (res.success) {
      posthog.capture("Launchable Deployed", extraEventProperties);
      if (!res.data || !res.data.id) {
        onFail(
          "Unknown error creating instance: No ID returned. Please try again in a few minutes",
        );
        return { deploymentStarted: false };
      }

      const environment = transformWorkspaceToEnvironment(res.data);
      if (!environment) {
        onFail("Failed to transform workspace to environment: " + res.message);
        return { deploymentStarted: false };
      }

      const hasStartupScript = !!(
        res.data?.startupScriptPath ||
        containerSelected?.vmBuild?.lifeCycleScriptAttr?.id
      );

      // Detect IngressGateway CTAs from the create response. These are not
      // in buildRequest.ports so hasCTAApp is false for them, but they are
      // present in environment.build.ingress_gateway from the initial response.
      const hasIngressCTA = hasCTAIngress(environment);
      const finalHasHealthCheck =
        environment.labels?.["tunnel-provider"] !== "skybridge" &&
        (hasInitialHealthCheck || hasIngressCTA);
      hasHealthCheckRef.current = finalHasHealthCheck;

      if (hasStartupScript) {
        hasStartupScriptRef.current = true;
      }

      // Regenerate steps with the full picture from the create response so the
      // panel always shows the correct steps from the first render.
      stepsToUse = buildSteps(hasStartupScript, finalHasHealthCheck);
      setSteps(stepsToUse);

      // Surface the panel only after steps are finalized.
      setInitLoading(false);
      setShowDeploying(true);
      setActiveStep(DeployState.Deploying);

      setEnvironment(environment);

      try {
        const runningWorkspace = await pollInstanceUntilSuccess(res.data.id);
        const runningEnvironment =
          transformWorkspaceToEnvironment(runningWorkspace);
        if (runningEnvironment) {
          setEnvironment(runningEnvironment);
          advanceActiveStepTo(
            getCurrentDeployStateFromEnvironment(runningEnvironment),
          );
        } else {
          advanceActiveStepTo(DeployState.Dependencies);
        }
      } catch (error) {
        onFail("Failed to create instance, Please try again in a few minutes");
        console.error("Failed to create instance:", error);
        return { deploymentStarted: true };
      }

      setLogFileContent("");

      // Only the default verbBuild needs the API response to know the python/cuda
      // versions; all other container types were already set in the initial steps.
      if (!dependencyName && res.data.verbYaml) {
        const verbYamlObj = yaml.load(res.data.verbYaml);
        updateDependencyList(
          `Installing Python ${verbYamlObj.build.python_version} & CUDA ${verbYamlObj.build.cuda}`,
          stepsToUse,
        );
      }
    } else {
      if (res.code === "CREDIT_EXHAUSTED") {
        setShowConnectCloudsOrCardModal(true);
        reset();
        return { deploymentStarted: false };
      }
      onFail("Failed to create instance. Please try again in a few minutes.");
      console.error("Failed to create instance");
      return { deploymentStarted: false };
    }

    return { deploymentStarted: true };
  }

  const environmentLogTarget = getEnvironmentLogTarget(
    environment,
    showDeploying,
    environmentLogHistoryEnabled,
  );

  useEffect(() => {
    setLogFileContent("");
  }, [environmentLogHistoryEnabled, environmentLogTarget.executionKey]);

  useEnvironmentLogs({
    environmentId: environmentLogTarget.environmentId,
    executionKey: environmentLogTarget.executionKey,
    mode: environmentLogTarget.mode,
    onLogs: (content, source) => {
      if (environmentLogHistoryEnabled || source === "persisted") {
        setLogFileContent(content);
      }
    },
    onError: (error) => {
      console.error(
        "[environment-logs] failed to load one-click deploy logs",
        error,
      );
    },
  });

  const lifecycleScriptRun = environment?.lifecycle_scripts?.[0]?.last_run;
  const lifecycleScriptStatus = lifecycleScriptRun?.status;
  const lifecycleScriptEnvironmentId =
    environment?.environment_id || environment?.workspaceId || "";
  const lifecycleScriptIsDone =
    lifecycleScriptStatus === LifecycleScriptStatus.COMPLETED ||
    lifecycleScriptStatus === LifecycleScriptStatus.FAILED;
  const { data: persistedLifecycleScriptLog } = useQuery({
    queryKey: [
      "one-click-lifecycle-script-environment-log",
      lifecycleScriptEnvironmentId,
      lifecycleScriptRun?.run_id,
    ],
    queryFn: async ({ signal }) => {
      const persisted = await fetchLatestPersistedEnvironmentLogsByType({
        environmentId: lifecycleScriptEnvironmentId,
        environmentLogType: ENVIRONMENT_LOG_TYPES.lifecycleScript,
        signal,
      });
      return new TextDecoder().decode(persisted.content);
    },
    enabled: Boolean(
      environmentLogHistoryEnabled &&
      showDeploying &&
      lifecycleScriptEnvironmentId &&
      lifecycleScriptRun?.run_id &&
      lifecycleScriptIsDone,
    ),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 3,
  });

  useEffect(() => {
    if (
      !environmentLogHistoryEnabled ||
      !persistedLifecycleScriptLog ||
      persistedLifecycleScriptLog === "\n"
    ) {
      return;
    }
    setLogFileContent((previous) =>
      previous.includes(persistedLifecycleScriptLog)
        ? previous
        : previous + persistedLifecycleScriptLog,
    );
  }, [environmentLogHistoryEnabled, persistedLifecycleScriptLog]);

  const { data: updatedWorkspace } = useQuery({
    queryKey: ["environment", environment?.workspaceId],
    queryFn: async () => {
      const res = await agent.Workspaces.get(environment?.workspaceId || "");
      if (res.success && res.data) {
        return transformWorkspaceToEnvironment(res.data);
      }
      return null;
    },
    refetchInterval: 3000,
    refetchIntervalInBackground: false,
    enabled: !!environment,
    staleTime: 1000,
  });

  // Sync polled workspace data into local state. Kept in a useEffect rather
  // than the query's onSuccess so it always sees fresh data — onSuccess in
  // React Query v4 can fire with stale cached values when staleTime > 0.
  useEffect(() => {
    if (!updatedWorkspace || !environment) return;

    if (hasPolledEnvironmentChanged(updatedWorkspace, environment)) {
      setEnvironment(updatedWorkspace);
    }
  }, [environment, updatedWorkspace]);

  useEffect(() => {
    if (environment) {
      advanceActiveStepTo(getCurrentDeployStateFromEnvironment(environment));
    }

    if (
      environment &&
      getEnvironmentStatus(environment) === WorkspaceStatus.Failure
    ) {
      onFail(
        parseStatusMessage(
          environment.instance?.provision_status?.message || "",
        ).trim() || "Unknown error",
      );
    }
  }, [
    advanceActiveStepTo,
    environment,
    getCurrentDeployStateFromEnvironment,
    onFail,
  ]);

  // Check verb container build status
  useEffect(() => {
    if (
      !environment ||
      getEnvironmentStatus(environment) !== WorkspaceStatus.Running
    )
      return;
    if (logFileContent.includes(VERB_FAILED_PHRASE)) {
      onFail("Failed to build container");
    }
    // vmBuild and other container types signal build completion via the log file.
    if (
      logFileContent.includes(VERB_FINISHED_PHRASE) &&
      !buildFinishedFromLogsRef.current
    ) {
      buildFinishedFromLogsRef.current = true;
      advanceActiveStepTo(
        getCurrentDeployStateFromEnvironment(environment, true),
      );
    }
  }, [
    advanceActiveStepTo,
    environment,
    getCurrentDeployStateFromEnvironment,
    logFileContent,
    onFail,
  ]);

  useEffect(() => {
    setNotebookApp(
      environment
        ? findNotebookApp(
            getIngressApplicationsFromEnvironment(environment),
            environment.workspaceId,
          )
        : undefined,
    );
  }, [environment]);

  const readyAction = async (notebookHostname = notebookApp?.hostname) => {
    if (!notebookHostname) {
      history.push(
        `/org/${orgContext.activeOrgId}/environments/${environment?.workspaceId}#Access`,
      );
      return;
    }

    if (environment?.build?.files && environment?.build?.files.length > 0) {
      const file = environment.build.files[0];
      if (
        file &&
        environment.build.verb &&
        !environment.build.verb?.config?.includes("workingDirectory")
      ) {
        window.open(
          "https://" +
            notebookHostname +
            "/lab/tree" +
            (file.file_directory?.startsWith("/") ? "" : "/") +
            file.file_directory,
          "_blank",
        );
      } else {
        window.open("https://" + notebookHostname, "_blank");
      }
    } else {
      window.open("https://" + notebookHostname, "_blank");
    }
  };

  const providerValues = {
    activeStep,
    setActiveStep,
    steps: steps,
    readyAction,
    hasNotebook,
    notebookApp,
    startDeployment,
    startDeploymentFromLaunchable,
    setIsFinished,
    isFinished,
    reset,
    showDeploying,
    setShowDeploying,
    showConnectCloudsOrCardModal,
    setShowConnectCloudsOrCardModal,
    noCardOnFile,
    setNoCardOnFile,
    selectedCloudName,
    setSelectedCloudName,
    environment,
    instanceTypeObject,
    requestedDiskStorage,
    initLoading,
    logFileContent,
    healthCheckFailed,
  };

  return (
    <OneClickDeployContext.Provider value={providerValues}>
      {children}
    </OneClickDeployContext.Provider>
  );
};

export default OneClickDeployContextProvider;
